import React from "react";
import cx from "classnames";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsBackButton from "./metrics-back-button";
import getDayRangeString from "../../../utils/day-range-string";
import set from "@rill/set";

import {
  PRIMARIES,
  RM_FORMULAS,
  LOAD_FORMULAS,
  DATE_FORMATS,
  PERCENTAGE_RM_TABLE,
} from "../../../constants";
import SvgIcon from "../../../parts/SvgIcon";
import NavigationComponent from "../calendar/components/NavigationComponent";
import { ReportsBackHandler } from "../calendar/components/ReportsBackHandler";
import { AthleteLinks } from "../../../parts/athlete-links";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import getAvatarUrl from "../../../utils/get-avatar-url";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { useTranslation } from "react-i18next";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const { DATE_DAY_FORMAT, DATE_PICKER_FORMAT } = DATE_FORMATS;

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("athlete-portal");

  const { query, search, pathname, athlete: athleteObj } = locals;
  const { units, display, formula, athlete } = query;
  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  const displayEstimated = display === "estimated";
  const hover = displayEstimated ? "actual" : "estimated";
  const exercisesRecordsLoad =
    query.type === "total"
      ? locals.exercisesRecordsLoad
      : _.pick(locals.exercisesRecordsLoad, [query.type]);

  return (
    <main role="reports-wrapper">
      <NavigationComponent
        query={query}
        pathName={"/athlete-portal/metrics"}
        textKey="nav.calendarDayView"
        classNames="mobile-hidden"
      />
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athleteObj}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/athlete-portal/metrics/${search}`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/athlete-portal/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/rep-max-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/rep-max-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3>{t("metrics.repMaxEstimator")}</h3>
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("metricsDrawer.kg"), value: "metric" },
                  { text: t("metricsDrawer.lbs"), value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                {t("repMaxEstimator.description")}
              </p>
            </div>
            <p className="p4 only-show-mobile">
              {t("repMaxEstimator.description")}
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder={t("reports.startDate")}
                value={query.start}
                max={query.end}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>{t("reports.to")}</span>
              <DateInput
                name="end"
                placeholder={t("reports.endDate")}
                value={query.end}
                min={query.start}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
          </hgroup>
          <ToggleLinksInput
            name="display"
            options={[
              { text: t("repMaxEstimator.estimated"), value: "estimated" },
              { text: t("repMaxEstimator.actual"), value: "actual" },
            ]}
            className={"only-show-mobile estimated-actual-toggle"}
            activePrimary
          />
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: t("metricsDrawer.kg"), value: "metric" },
                { text: t("metricsDrawer.lbs"), value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />
            <ToggleLinksInput
              name="display"
              options={[
                { text: t("repMaxEstimator.estimated"), value: "estimated" },
                { text: t("repMaxEstimator.actual"), value: "actual" },
              ]}
              className={"mobile-hidden"}
            />
            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("strengthMetricsShared.total"), value: "total" },
                { text: t("strengthMetricsShared.squat"), value: "squat" },
                { text: t("strengthMetricsShared.bench"), value: "bench" },
                { text: t("strengthMetricsShared.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>
        <div className="top-indicator-set">
          <p className="s1">{t("repMaxEstimator.topIndicatorSet")}</p>
        </div>
        <section role="table-wrapper">
          <table role="report-table" className="rep-max-table">
            <thead>
              <tr>
                <th>
                  <span>{t("repMaxEstimator.exerciseHeader")}</span>
                </th>
                <th>{t("repMaxEstimator.oneRep")}</th>
                <th>2</th>
                <th>3</th>
                <th>4</th>
                <th>5</th>
                <th>6</th>
                <th>7</th>
                <th>8</th>
                <th>9</th>
                <th>10</th>
                <th>11</th>
                <th>12</th>
              </tr>
            </thead>
            <tbody>
              {_.flatMap(PRIMARIES, (primary) => {
                const secondaries = exercisesRecordsLoad[primary];
                return _(secondaries)
                  .map((reps, secondary) => {
                    return { reps, secondary };
                  })
                  .sortBy("secondary")
                  .map(({ reps, secondary }) => {
                    const secondaryLabel = secondary ? `(${secondary})` : "";
                    let bestRep = 0;
                    let best1RepMax = 0;

                    _.forEach(reps, ({ load, e1rm }, rep) => {
                      const repMax = e1rm ? e1rm : loadTo1RepMax(load, rep);
                      if (repMax > best1RepMax) {
                        best1RepMax = repMax;
                        bestRep = Number(rep);
                      }
                    });

                    return (
                      <tr key={primary + secondary}>
                        <td>
                          {_.capitalize(primary)} {secondaryLabel}
                        </td>
                        {_.map(_.range(1, 13), (i) => {
                          const isBest = bestRep === i;
                          let date =
                            _.has(reps[i], "date") && moment.utc(reps[i].date);

                          if (displayEstimated && !isBest) date = null;

                          const loads = {
                            actual: _.get(reps[i], "load"),
                            estimated: getEstimatedRepMax(
                              best1RepMax,
                              i,
                              reps,
                              displayEstimated
                            ),
                          };

                          const displayText = _.round(
                            locals.fromMetric(loads[display], units, false),
                            1
                          )
                            ? _.round(
                                locals.fromMetric(loads[display], units, false),
                                1
                              ) + `${units === "metric" ? " kg" : " lbs"}`
                            : "-";
                          const hoverText = `<span>${_.capitalize(
                            hover
                          )}</span> ${
                            _.round(
                              locals.fromMetric(loads[hover], units, false),
                              1
                            ) || "-"
                          }`;
                          return (
                            <td key={i} className={cx({ best: isBest })}>
                              {date ? (
                                <span>
                                  <a
                                    href={
                                      `/${
                                        athlete
                                          ? `athlete-portal/calendar/?athlete=${athlete}&`
                                          : "dashboard/calendar/?"
                                      }view=day&date=${date.format(
                                        DATE_DAY_FORMAT
                                      )}` + organizationCoachParam
                                    }
                                    className="metrics-table-link"
                                  >
                                    {displayText}
                                  </a>
                                  <div
                                    className="tooltip"
                                    dangerouslySetInnerHTML={{
                                      __html: hoverText,
                                    }}
                                  />
                                </span>
                              ) : (
                                <span>
                                  <span>{displayText}</span>
                                  <div
                                    className="tooltip"
                                    dangerouslySetInnerHTML={{
                                      __html: hoverText,
                                    }}
                                  />
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                  .value();
              })}
            </tbody>
          </table>
        </section>
      </article>
    </main>
  );

  function loadTo1RepMax(load, reps) {
    // @Dylan - I have commented out this line as according to Mike, the 1RM calculation should also be performed on the 1 rep value
    // if (Number(reps) === 1) return load

    return RM_FORMULAS[formula](load, reps);
  }

  function repMaxToLoad(rm, reps) {
    return LOAD_FORMULAS[formula](rm, reps);
  }

  function getEstimatedRepMax(best1RepMax, rep, reps, displayEstimated) {
    if (!displayEstimated) {
      return _.get(reps[rep], "e1rm");
    }
    return best1RepMax * PERCENTAGE_RM_TABLE[rep];
  }
}

function handleDayChange(date, field) {
  submit("/actions/refresh", {
    method: "GET",
    body: { [field]: date },
  });
}

const RepMaxEstimator = (app) => {
  app.get(
    "/rep-max-estimator",
    set("heading", "REP Max Estimator"),
    async ({ req, res, load, locals, api, fail }) => {
      const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
        locals.organizationRoleName
      );

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        await load("allAthletesFromOrganization");
      }

      const { query } = req;
      const user = locals.activeUser._id;
      _.defaults(query, {
        type: "total",
        formula: "MyStrengthBook",
        display: "estimated",
        units: locals.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await load("exercisesRecordsLoad", {
        start: moment
          .utc(query.start, DATE_PICKER_FORMAT)
          .startOf("day")
          .toDate(),
        end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
        proxyAthlete: query.athlete,
        reqSource: "metrics",
      });

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  user: {
                    name: locals.athlete.name,
                    registered: locals.athlete.registered,
                    avatar: locals.athlete.avatar,
                    units: locals.athlete.units,
                  },
                  type: "rep-max-estimator",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesRecordsLoad"])
                      .assign({
                        formula: query.formula,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);

          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }

      locals.modal = {
        share: <ShareModal />,
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default RepMaxEstimator;
